"use client"

import { useState, useContext, useEffect, useRef } from "react"
import { ErpContext } from "../../context/ErpContext.js"
import {
  Autocomplete,
  TextField,
  Button,
  Box,
  FormControlLabel,
  Checkbox,
  IconButton,
  InputAdornment,
  Stack,
  Tooltip,
  Typography,
  alpha,
  useTheme,
  Paper,
  Chip,
} from "@mui/material"

import SearchIcon from "@mui/icons-material/Search"
import FilterAltIcon from "@mui/icons-material/FilterAlt"
import WarehouseIcon from "@mui/icons-material/Warehouse"
import LocationOnIcon from "@mui/icons-material/LocationOn"
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff"
import FilterListIcon from "@mui/icons-material/FilterList"
import WarningIcon from "@mui/icons-material/Warning"
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import ClearIcon from "@mui/icons-material/Clear"
import PersonIcon from "@mui/icons-material/Person"
import BusinessIcon from "@mui/icons-material/Business"

const BuscadorItemsUbicaciones = ({ 
  onSearch, 
  vieneDe, 
  ubicacionBloqueada, 
  onUbicacionChange, 
  labelBusqueda, 
  modoPaquetes, 
  grupoSeleccionado,
  ubicacionInicial = null,  // NUEVA PROP para pre-cargar ubicación
  modoEdicion = false,      // NUEVA PROP para saber si está en modo edición
  sinPaper = false,         // NUEVA PROP para no renderizar el Paper interno
  ocultarUbicacion = false, // NUEVA PROP para ocultar el campo de ubicación/destino
  resetKey = 0,             // NUEVA PROP: trigger externo para resetear filtros
  unaLinea = false,         // NUEVA PROP: forzar layout en una sola fila
  mostrarSelectorProveedor = false, // NUEVA PROP: mostrar selector de proveedor
  proveedorSeleccionado = null,     // NUEVA PROP: proveedor actual seleccionado
  onProveedorChange = null,         // NUEVA PROP: callback cuando cambia proveedor
  extraAfterUbicacion = null,       // NUEVA PROP: contenido extra después de ubicación
  onReset = null,                   // NUEVA PROP: callback cuando se limpian filtros
  extraActionButtons = null,        // NUEVA PROP: acciones extra junto a Buscar/Limpiar
  containerSx = undefined,          // NUEVA PROP: estilos para el contenedor raíz
  sinContenedorInterno = false,     // NUEVA PROP: devolver hijos sin Stack wrapper
  dropdownEsServicio = null,        // NUEVA PROP: null=default, true=solo servicios, false=solo grupos
}) => {
  const theme = useTheme()
  const isVerServicios = vieneDe === "inventario"

  const {
    getItemTresLetras,
    itemTresLetras,
    arrayItemTresLetras,
    getItemAtributoNoEliminados,
    itemAtributoNoEliminados,
    ubicaciones,
    getUbicacion,
    isNavbarCollapsed,
    entidadesClientesTresLetras,
    getEntidadesClientesTresLetras,
    getItemAndItemUbicacion,              // Para buscar grupos (esServicio: false)
    getItemAndItemUbicacionServicios,     // Para buscar servicios (esServicio: true)
    getItemUbicacionFilter,               // Para obtener ubicaciones filtradas por item
    itemUbicacionFilter,                   // Estado de ubicaciones filtradas
    getEntidadByID,
  } = useContext(ErpContext)

  const [selectedUbicacion, setSelectedUbicacion] = useState(ubicacionInicial)
  const [precioItem, setPrecioItem] = useState("")
  const [searchText, setSearchText] = useState("")
  const [selectedItem, setSelectedItem] = useState(null)
  const [soloStockBajo, setSoloStockBajo] = useState(false)
  const [mostrarUltimosCreados, setMostrarUltimosCreados] = useState(false)
  const [mostrarRecetas, setMostrarRecetas] = useState(false)
  const [selectedProveedor, setSelectedProveedor] = useState(null)
  const [searchTextProveedor, setSearchTextProveedor] = useState("")
  const [ubicacionesFiltradas, setUbicacionesFiltradas] = useState([])
  
  const autocompleteRef = useRef(null)
  const isFirstRender = useRef(true)

  const generateItemLabel = (item) => {
    const activeAttributes = itemAtributoNoEliminados.filter((attr) => !attr.eliminado)
    const attributeValues = activeAttributes
      .map((attr, index) => {
        const attributeValue = item[`itemDatoAtributo${index + 1}`]
        return attributeValue ? attributeValue : ""
      })
      .join(" ")
    return `${item.codigo} - ${item.descripcion} - ${attributeValues}` // - ${item.codigoScanner}`
  }

  useEffect(() => {
    if (searchText.length >= 3) {
      arrayItemTresLetras([])
      
      // Si estamos en modo paquetes, buscar según el contexto
      if (modoPaquetes) {
        if (!grupoSeleccionado) {
          // Primera búsqueda: solo grupos (esServicio: false)
          buscarItemsPorTipo(searchText, false)
        } else {
          // Segunda búsqueda: solo servicios (esServicio: true)
          buscarItemsPorTipo(searchText, true)
        }
      } else if (isVerServicios) {
        // verServicios: solo servicios (esServicio: true)
        getItemTresLetras(searchText, false, true)
      } else {
        // Búsqueda normal (grupos + servicios)
        getItemTresLetras(searchText)
      }
    } else {
      arrayItemTresLetras([])
    }
  }, [searchText, modoPaquetes, grupoSeleccionado, isVerServicios])
  
  // Función para buscar items por tipo (grupos o servicios) - BUSCA DIRECTAMENTE EN TABLA ITEM
  const buscarItemsPorTipo = async (texto, esServicio) => {
    try {
      // Usar getItemTresLetras que busca directamente en tabla Item
      const resultado = await getItemTresLetras(texto)
      
      if (resultado && resultado.items) {
        // Filtrar por esServicio para evitar duplicados
        const itemsFiltrados = resultado.items.filter(item => item.esServicio === esServicio)
        console.log(`Búsqueda ${esServicio ? 'SERVICIOS' : 'GRUPOS'}: ${itemsFiltrados.length} items encontrados`)
        arrayItemTresLetras(itemsFiltrados)
      }
    } catch (error) {
      console.error("Error buscando items por tipo:", error)
    }
  }

  // Búsqueda de proveedores (tipo entidad 2)
  useEffect(() => {
    if (searchTextProveedor.length >= 3) {
      getEntidadesClientesTresLetras(searchTextProveedor, [2])
    }
  }, [searchTextProveedor])

  useEffect(() => {
    if (searchText.length >= 3 && itemTresLetras.length > 0) {
      const found = itemTresLetras.find((item) => {
        if (dropdownEsServicio === true) return item.esServicio === true && item.codigoScanner === searchText
        if (dropdownEsServicio === false) return item.esServicio !== true && item.codigoScanner === searchText
        if (vieneDe === "inventario" && item.esServicio !== true) return false
        return item.codigoScanner === searchText
      })
      if (found) {
        const autoSelected = {
          label: generateItemLabel(found),
          descripcion: found.descripcion,
          codigo: found.codigo,
          id: found.id,
          codigoScanner: found.codigoScanner,
        }
        setSelectedItem(autoSelected)
      }
    }
  }, [searchText, itemTresLetras, vieneDe, dropdownEsServicio])

  useEffect(() => {
    const fetchData = async () => {
      if (isFirstRender.current) {
        isFirstRender.current = false
        return
      }
      try {
        await getUbicacion()
        await getItemAtributoNoEliminados()
      } catch (error) {
        console.error(error)
      }
    }
    fetchData()
  }, [])

  // USEEFFECT: Actualizar ubicación cuando viene ubicacionInicial (modo edición)
  useEffect(() => {
    if (ubicacionInicial) {
      setSelectedUbicacion(ubicacionInicial)
      console.log("Ubicación inicial cargada en buscador:", ubicacionInicial)
    }
  }, [ubicacionInicial])

  // Cargar ubicaciones filtradas cuando se selecciona un item
  useEffect(() => {
    const cargarUbicacionesFiltradas = async () => {
      if (selectedItem?.id) {
        console.log(`Cargando ubicaciones para item ${selectedItem.id}`)
        try {
          const soloDestinos = isVerServicios
          const itemsUbic = await getItemUbicacionFilter(selectedItem.id, soloDestinos)
          setUbicacionesFiltradas(itemsUbic || [])
        } catch (error) {
          console.error("Error al cargar ubicaciones filtradas:", error)
          setUbicacionesFiltradas([])
        }
      } else {
        setUbicacionesFiltradas([])
      }
    }
    
    cargarUbicacionesFiltradas()
  }, [selectedItem?.id, isVerServicios])

  const handleItemChange = (event, newValue) => {
    setSelectedItem(newValue)
    if (dropdownEsServicio === true && newValue?.descripcion) {
      setSearchText(newValue.descripcion)
    }
     // AUTO-AGREGAR: Ejecutar búsqueda automáticamente cuando se selecciona un item
     if (newValue && newValue.id) {
      const filters = {
        itemId: newValue.id,
        ubicacionId: selectedUbicacion ? selectedUbicacion.id : null,
        precio: precioItem !== "" ? Number(precioItem) : null,
        soloStockBajo,
        mostrarUltimosCreados,
        mostrarRecetas,
        proveedorId: selectedProveedor ? selectedProveedor.id : null,
        searchText: null,
        // NUEVO: Enviar también los datos del item seleccionado
        itemData: {
          id: newValue.id,
          descripcion: newValue.descripcion || '',
          codigo: newValue.codigo || '',
          codigoScanner: newValue.codigoScanner || null,
          esServicio: newValue.esServicio || false
        }
      }
      console.log("Auto-ejecutando búsqueda para item seleccionado:", newValue.descripcion)
      onSearch(filters)
      
      // Resetear el campo de búsqueda después de agregar (excepto inventario)
      if (vieneDe !== "inventario") {
        setTimeout(() => {
          setSearchText("")
          setSelectedItem(null)
        }, 100) // Pequeño delay para que se procese la búsqueda primero
      }
    }
  }

  const handleFilter = () => {
    // Nueva funcionalidad: si hay texto de búsqueda pero no hay item seleccionado,
    // realizar búsqueda múltiple
    const searchTextForMultiple = searchText && searchText.trim() !== "" && !selectedItem ? searchText.trim() : null;
    
    const filters = {
      itemId: selectedItem ? selectedItem.id : null,
      ubicacionId: selectedUbicacion ? selectedUbicacion.id : null,
      precio: precioItem !== "" ? Number(precioItem) : null,
      soloStockBajo,
      mostrarUltimosCreados,
      mostrarRecetas,
      proveedorId: selectedProveedor ? selectedProveedor.id : null,
      searchText: searchTextForMultiple, // Nuevo parámetro para búsqueda múltiple
    }
    // console.log("Filtros enviados desde Buscador:", filters)
    onSearch(filters)
  }

  // Función para resetear todos los campos a sus valores iniciales
  const handleReset = () => {
    setSelectedUbicacion(null)
    if (onUbicacionChange) {
      onUbicacionChange(null)
    }
    setPrecioItem("")
    setSearchText("")
    setSelectedItem(null)
    setSoloStockBajo(false)
    setMostrarUltimosCreados(false)
    setMostrarRecetas(false)
    setSelectedProveedor(null)
    setSearchTextProveedor("")

    // Enviar filtros vacíos para resetear la búsqueda
    onSearch({
      itemId: null,
      ubicacionId: null,
      precio: null,
      soloStockBajo: false,
      mostrarUltimosCreados: false,
      mostrarRecetas: false,
      proveedorId: null,
      searchText: null, // Resetear también el texto de búsqueda múltiple
      soloItemsConPLU: false, // También desactiva el filtro de solo PLU
    })
    if (onReset) {
      onReset()
    }
  }

  // Reset externo (ej: botón "Resetear filtros" del padre o después de agregar item)
  useEffect(() => {
    if (!resetKey) return
    
    // Resetear solo el campo de búsqueda (no otros filtros como ubicación)
    setSearchText("")
    setSelectedItem(null)
    
    // No llamar a handleReset() completo para no resetear ubicación y otros filtros
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey])

  // Verificar si hay filtros activos para mostrar indicador
  const hasActiveFilters = selectedItem || selectedUbicacion || precioItem || soloStockBajo || mostrarUltimosCreados || mostrarRecetas || selectedProveedor

  const stackDirection = unaLinea ? "row" : { xs: "column", lg: "row" }

  const contenido = (
    <Stack
      direction={stackDirection}
      spacing={1.5}
      alignItems="stretch"
      sx={[
        unaLinea
          ? {
              flexWrap: "wrap", // Permitir wrap en lugar de scroll
              gap: 1,
            }
          : undefined,
        containerSx,
      ]}
    >
        {/* En modo paquetes, mostrar ubicación primero */}
        {modoPaquetes && !ocultarUbicacion && (
          <Box sx={{ flex: { xs: "1 1 100%", sm: "0 1 250px" }, minWidth: { xs: "100%", sm: 250 }, position: "relative", zIndex: 1000 }}>
            <Autocomplete
              size="small"
              disablePortal={false}
              disabled={modoEdicion ? true : (ubicacionBloqueada || false)}
              options={(selectedItem && ubicacionesFiltradas.length > 0
                ? ubicacionesFiltradas
                    .filter((it) => {
                      const ub = ubicaciones.find(u => u.id === it.idUbicacion)
                      return ub?.esDestino === true
                    })
                    .map((it) => ({
                      id: it.idUbicacion,
                      label: it.ubicacion?.descripcion || ubicaciones.find(u => u.id === it.idUbicacion)?.descripcion || "Sin descripción",
                    }))
                : ubicaciones
                    .filter((it) => it.esDestino === true)
                    .map((it) => ({
                      id: it.id,
                      label: it.descripcion,
                    })))}
              value={selectedUbicacion}
              onChange={(e, newValue) => {
                setSelectedUbicacion(newValue)
                if (onUbicacionChange) {
                  onUbicacionChange(newValue)
                }
              }}
              getOptionLabel={(option) => option?.label || option?.descripcion || ""}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              componentsProps={{
                popper: {
                  sx: {
                    zIndex: 10000, // Z-index muy alto para asegurar visibilidad
                  },
                  placement: "bottom-start",
                  disablePortal: false,
                },
                paper: {
                  sx: {
                    maxHeight: 300,
                  },
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Destino"
                  variant="outlined"
                  placeholder="Seleccionar..."
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <WarehouseIcon color={ubicacionBloqueada ? "disabled" : "action"} fontSize="small" />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                    endAdornment: selectedUbicacion && !ubicacionBloqueada && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedUbicacion(null)
                            if (onUbicacionChange) {
                              onUbicacionChange(null)
                            }
                          }}
                          sx={{ color: theme.palette.text.secondary }}
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1.5,
                      backgroundColor: ubicacionBloqueada 
                        ? alpha(theme.palette.grey[300], 0.5)
                        : alpha(theme.palette.background.paper, 0.7),
                      "&:hover": {
                        backgroundColor: ubicacionBloqueada 
                          ? alpha(theme.palette.grey[300], 0.3)
                          : theme.palette.background.paper,
                      },
                      "&.Mui-focused": {
                        backgroundColor: ubicacionBloqueada 
                          ? alpha(theme.palette.grey[300], 0.3)
                          : theme.palette.background.paper,
                      },
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "0.875rem",
                    },
                  }}
                />
              )}
            />
          </Box>
        )}
        
        {/* Búsqueda de ítem */}
        <Box
          sx={{
            flex: isVerServicios ? { xs: "1 1 100%", sm: "0 1 240px" } : { xs: "1 1 100%", sm: "1 1 auto" },
            padding: 0,
            minWidth: { xs: "100%", sm: isVerServicios ? 240 : 280 },
            maxWidth: { sm: isVerServicios ? 260 : "none" },
            position: "relative",
            zIndex: 999,
          }}
        >
          <Autocomplete
            size="small"
            disablePortal={false}
            freeSolo
            forcePopupIcon={isVerServicios ? false : undefined}
            clearIcon={isVerServicios ? <ClearIcon fontSize="small" /> : undefined}
            disabled={modoPaquetes && !selectedUbicacion && !ocultarUbicacion} // En modo paquetes, requiere ubicación (excepto si está oculta)
            options={(itemTresLetras || [])
              .filter((it) => {
                if (dropdownEsServicio === true) return it.esServicio === true
                if (dropdownEsServicio === false) return it.esServicio !== true
                return !(vieneDe === "inventario" && it.esServicio === true)
              })
              .map((it) => {
              const activeAttributes = itemAtributoNoEliminados.filter((attr) => !attr.eliminado)
              const attributeValues = activeAttributes
                .map((attr, index) => {
                  const attributeValue = it[`itemDatoAtributo${index + 1}`]
                  return attributeValue ? attributeValue : ""
                })
                .filter((val) => val.trim() !== "")
                .join(" ")
              
              return {
                label: generateItemLabel(it),
                descripcion: it.descripcion,
                codigo: it.codigo,
                id: it.id,
                idItem: it.idItem || it.id,
                codigoScanner: it.codigoScanner,
                esServicio: it.esServicio,
                atributos: attributeValues,
              }
            })}
            value={selectedItem}
            onChange={(event, newValue) => {
              if (typeof newValue === 'string') {
                setSearchText(newValue);
                setSelectedItem(null);
              } else {
                handleItemChange(event, newValue);              
              }
            }}
            onInputChange={(event, newInputValue, reason) => {
              if (dropdownEsServicio === true && reason === "reset") {
                return
              }
              setSearchText(newInputValue);
              if (!newInputValue || newInputValue.trim() === '') {
                setSelectedItem(null);
              }
            }}
            inputValue={searchText}
            componentsProps={{
              popper: {
                sx: {
                  zIndex: 9999,
                  "& .MuiAutocomplete-listbox": {
                    maxHeight: 300, // Mayor altura para ver más opciones
                  },
                },
                placement: "bottom-start", // Forzar que abra hacia abajo
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={modoPaquetes 
                  ? (grupoSeleccionado ? "Buscar servicio..." : "Buscar grupo (paquete)...") 
                  : (labelBusqueda || "Buscar servicio (Enter para múltiples resultados)")}
                variant="outlined"
                placeholder="Nombre, descripción ..."
                
                InputLabelProps={{ shrink: true }}
                onKeyPress={(event) => {
                  if (event.key === 'Enter' && searchText && searchText.trim() !== "" && !selectedItem) {
                    event.preventDefault();
                    handleFilter();
                  }
                }}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <InputAdornment position="start">
                        <SearchIcon color="action" fontSize="small" />
                      </InputAdornment>
                      {params.InputProps.startAdornment}
                    </>
                  ),
                  endAdornment:
                    isVerServicios
                      ? params.InputProps.endAdornment
                      : (selectedItem || (searchText && searchText.trim() !== "")) && (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedItem(null);
                                setSearchText("");
                              }}
                              sx={{ color: theme.palette.text.secondary }}
                            >
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 1.5,
                    backgroundColor: (modoPaquetes && !selectedUbicacion && !ocultarUbicacion)
                      ? alpha(theme.palette.grey[300], 0.5)
                      : alpha(theme.palette.background.paper, 0.7),
                    "&:hover": {
                      backgroundColor: (modoPaquetes && !selectedUbicacion && !ocultarUbicacion)
                        ? alpha(theme.palette.grey[300], 0.3)
                        : theme.palette.background.paper,
                    },
                    "&.Mui-focused": {
                      backgroundColor: (modoPaquetes && !selectedUbicacion && !ocultarUbicacion)
                        ? alpha(theme.palette.grey[300], 0.3)
                        : theme.palette.background.paper,
                    },
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "0.875rem",
                  },
                  ...(isVerServicios && {
                    "& .MuiAutocomplete-endAdornment": {
                      right: 8,
                    },
                  }),
                }}
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props} sx={{ fontSize: "0.875rem", py: 0.5 }}>
                <Box>
                  <Box sx={{ fontWeight: 500 }}>
                    {option.descripcion} {option.atributos && option.atributos.trim() !== "" && (
                      <span style={{ fontWeight: 400, color: "#666" }}>• {option.atributos}</span>
                    )}
                  </Box>
                  <Box sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                    {option.codigo} • {option.codigoScanner}
                  </Box>
                </Box>
              </Box>
            )}
          />
        </Box>

        {/* Selector de Proveedor - Recibe props desde el padre */}
        {mostrarSelectorProveedor && onProveedorChange && (
          <Box sx={{ flex: { xs: "1 1 100%", sm: "0 1 250px" }, minWidth: { xs: "100%", sm: 250 }, position: "relative", zIndex: 997 }}>
            <Autocomplete
              size="small"
              disablePortal={false}
              options={entidadesClientesTresLetras || []}
              getOptionLabel={(opt) => {
                if (!opt) return ""
                const partes = []
                if (opt.razonSocial) partes.push(opt.razonSocial)
                if (opt.nombre) partes.push(opt.nombre)
                if (opt.descripcion) partes.push(opt.descripcion)
                if (opt.apellido) partes.push(opt.apellido)
                return partes.join(" - ") || "Sin nombre"
              }}
              value={proveedorSeleccionado}
              onChange={(e, newValue) => {
                onProveedorChange(newValue)
              }}
              onInputChange={(event, newInputValue, reason) => {
                // Solo actualizar búsqueda cuando el usuario escribe
                if (reason === "input" && newInputValue && newInputValue.length >= 3) {
                  getEntidadesClientesTresLetras(newInputValue, 2)
                }
              }}
              componentsProps={{
                popper: {
                  sx: {
                    zIndex: 10000,
                  },
                  placement: "bottom-start",
                  disablePortal: false,
                },
                paper: {
                  sx: {
                    maxHeight: 300,
                  },
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Proveedor"
                  variant="outlined"
                  placeholder="Buscar proveedor"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <BusinessIcon sx={{ fontSize: 18 }} color="action" />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1,
                      fontSize: "0.875rem",
                      backgroundColor: alpha(theme.palette.background.paper, 0.5),
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "0.875rem",
                    },
                  }}
                />
              )}
            />
          </Box>
        )}

        {/* Ubicación (solo si NO es modo paquetes) */}
        {!modoPaquetes && !ocultarUbicacion && (
          <Box
            sx={{
              flex: { xs: "1 1 100%", sm: `0 1 ${isVerServicios ? 200 : 250}px` },
              minWidth: { xs: "100%", sm: isVerServicios ? 200 : 250 },
              maxWidth: { sm: isVerServicios ? 200 : "none" },
              position: "relative",
              zIndex: 998,
            }}
          >
            <Autocomplete
              size="small"
              disablePortal={false}
              forcePopupIcon={isVerServicios ? false : undefined}
              clearIcon={isVerServicios ? <ClearIcon sx={{ fontSize: 16 }} /> : undefined}
              disabled={ubicacionBloqueada || false}
              options={(selectedItem && ubicacionesFiltradas.length > 0
                ? ubicacionesFiltradas.map((it) => ({
                    id: it.idUbicacion,
                    label: it.ubicacion?.descripcion || ubicaciones.find(u => u.id === it.idUbicacion)?.descripcion || "Sin descripción",
                  }))
                : (isVerServicios ? ubicaciones.filter((u) => u.esDestino === true) : ubicaciones).map((it) => ({
                    id: it.id,
                    label: it.descripcion,
                  })))}
              value={selectedUbicacion}
              onChange={(e, newValue) => {
                setSelectedUbicacion(newValue)
                if (onUbicacionChange) {
                  onUbicacionChange(newValue)
                }
              }}
              componentsProps={{
                popper: {
                  sx: {
                    zIndex: 10000,
                  },
                  placement: "bottom-start",
                  disablePortal: false,
                },
                paper: {
                  sx: {
                    maxHeight: 300,
                  },
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Destino"
                  variant="outlined"
                  placeholder="Seleccionar..."
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          {isVerServicios ? (
                            <LocationOnIcon color={ubicacionBloqueada ? "disabled" : "action"} sx={{ fontSize: 18 }} />
                          ) : (
                            <WarehouseIcon color={ubicacionBloqueada ? "disabled" : "action"} sx={{ fontSize: 18 }} />
                          )}
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                    endAdornment:
                      isVerServicios
                        ? params.InputProps.endAdornment
                        : selectedUbicacion && !ubicacionBloqueada && (
                            <InputAdornment position="end">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setSelectedUbicacion(null)
                                  if (onUbicacionChange) {
                                    onUbicacionChange(null)
                                  }
                                }}
                                sx={{ color: theme.palette.text.secondary }}
                              >
                                <ClearIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </InputAdornment>
                          ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1,
                      fontSize: "0.875rem",
                      minHeight: isVerServicios ? 40 : undefined,
                      height: isVerServicios ? 40 : undefined,
                      alignItems: isVerServicios ? "center" : undefined,
                      backgroundColor: ubicacionBloqueada 
                        ? alpha(theme.palette.grey[300], 0.3)
                        : alpha(theme.palette.background.paper, 0.5),
                      "&:hover": {
                        backgroundColor: ubicacionBloqueada 
                          ? alpha(theme.palette.grey[300], 0.3)
                          : theme.palette.background.paper,
                      },
                      "&.Mui-focused": {
                        backgroundColor: ubicacionBloqueada 
                          ? alpha(theme.palette.grey[300], 0.3)
                          : theme.palette.background.paper,
                      },
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "0.875rem",
                    },
                    ...(isVerServicios && {
                      "& .MuiAutocomplete-endAdornment": {
                        right: 8,
                      },
                    }),
                  }}
                />
              )}
            />
          </Box>
        )}

        {extraAfterUbicacion}

        {/* Proveedor - Solo para inventario - OCULTO */}
        {/* {vieneDe === "inventario" && (
          <Box sx={{ flex: 0.8, minWidth: 200, position: "relative", zIndex: 8 }}>
            <Autocomplete
              size="small"
              disablePortal
              options={entidadesClientesTresLetras.map((it) => ({
                label: `${it.descripcion} - ${it.apellido} - ${it.dniCuitCuil}`,
                descripcion: it.descripcion,
                apellido: it.apellido,
                dniCuitCuil: it.dniCuitCuil,
                id: it.id,
              }))}
              value={selectedProveedor}
              onChange={(e, newValue) => setSelectedProveedor(newValue)}
              onInputChange={(e, newInputValue) => setSearchTextProveedor(newInputValue)}
              componentsProps={{
                popper: {
                  sx: {
                    zIndex: 10000, // Z-index alto
                  },
                  placement: "bottom-start",
                  disablePortal: false,
                },
                paper: {
                  sx: {
                    maxHeight: 300,
                  },
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Proveedor"
                  variant="outlined"
                  placeholder="Buscar proveedor..."
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <PersonIcon color="action" fontSize="small" />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                    endAdornment: selectedProveedor && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setSelectedProveedor(null)}
                          sx={{ color: theme.palette.text.secondary }}
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1.5,
                      backgroundColor: alpha(theme.palette.background.paper, 0.7),
                      "&:hover": {
                        backgroundColor: theme.palette.background.paper,
                      },
                      "&.Mui-focused": {
                        backgroundColor: theme.palette.background.paper,
                      },
                    },
                  }}
                />
              )}
            />
          </Box>
        )} */}

        {/* Botones de acción y estado */}
        <Box
          sx={{
            display: "flex",
            gap: 1,
            alignItems: "center",
            flexWrap: "wrap",
            flex: { xs: "1 1 100%", sm: "0 0 auto" },
            ml: unaLinea && !isVerServicios ? { xs: 0, sm: "20px" } : 0,
          }}
        >
          {hasActiveFilters && vieneDe == "inventario" && !unaLinea && (
            <Chip
              label={`${
                [selectedItem, selectedUbicacion, precioItem, soloStockBajo, mostrarUltimosCreados, mostrarRecetas, selectedProveedor].filter(Boolean)
                  .length
              }`}
              size="small"
              color="primary"
              variant="filled"
              sx={{
                fontSize: "0.7rem",
                height: 24,
                "& .MuiChip-label": { px: 1 },
              }}
            />
          )}

          <Button
            variant="contained"
            size="small"
            onClick={handleFilter}
            sx={{
              borderRadius: 1.5,
              textTransform: "none",
              backgroundColor: "#5b5b5f",
              fontWeight: 600,
              fontSize: "0.875rem",
              px: 1.5,
              py: 0.75,
              whiteSpace: "nowrap",
              boxShadow: "0 2px 8px rgba(91, 91, 95, 0.25)",
              "&:hover": {
                backgroundColor: theme.palette.secondary.main,
                transform: "translateY(-1px)",
                boxShadow: "0 4px 12px rgba(91, 91, 95, 0.35)",
              },
              transition: "all 0.2s ease",
            }}
          >
            {vieneDe == "inventario" ? "Buscar" : "Agregar"}
          </Button>

          <Tooltip title="Limpiar filtros">
            <IconButton
              onClick={handleReset}
              size="small"
              sx={{
                color: isVerServicios ? "#f4930e" : theme.palette.error.main,
                backgroundColor: isVerServicios ? "white" : alpha(theme.palette.error.main, 0.05),
                border: isVerServicios ? `1px solid ${alpha(theme.palette.grey[500], 0.6)}` : "none",
                borderRadius: 1.5,
                width: isVerServicios ? 34 : "auto",
                height: isVerServicios ? 34 : "auto",
                p: isVerServicios ? 0 : 0.5,
                "&:hover": {
                  backgroundColor: isVerServicios
                    ? alpha(theme.palette.grey[200], 0.4)
                    : alpha(theme.palette.error.main, 0.1),
                  transform: "scale(1.05)",
                },
                transition: "all 0.2s ease",
              }}
            >
              {isVerServicios ? <FilterListIcon sx={{ fontSize: 18 }} /> : <FilterAltOffIcon sx={{ fontSize: 18 }} />}
            </IconButton>
          </Tooltip>

          {extraActionButtons}
        </Box>
      </Stack>
  )

  const contenidoRender = sinContenedorInterno ? contenido.props.children : contenido

  if (sinPaper) {
    return contenidoRender
  }

  return (
    <Paper
      elevation={1}
      sx={{
        p: 1.5,
        borderRadius: 2,
        position: "relative",
        overflow: "visible", // Visible para Autocomplete
        zIndex: 100, // Z-index alto para asegurar visibilidad
        border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
        backgroundColor: alpha(theme.palette.grey[50], 0.5),
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 0,
        },
      }}
    >
      {contenidoRender}
    </Paper>
  )
}

export default BuscadorItemsUbicaciones
